import { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, X, KeyRound } from "lucide-react";

export default function ChangePasswordModal({ onClose }) {
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation("common");
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError(t("changePassword.mismatch")); return; }
    if (pw.length < 6) { setError(t("reset.tooShort")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pw });
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("changePassword.wrong"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="change-password-modal" className="bg-white rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold text-zinc-900 inline-flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> {t("changePassword.title")}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div data-testid="change-password-done" className="p-6 text-center space-y-2">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <div className="text-sm font-medium text-zinc-900">{t("changePassword.successToast")}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            {error && (
              <div data-testid="change-password-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("changePassword.current")}</label>
              <input data-testid="change-current" required type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" autoFocus />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("changePassword.new")}</label>
              <input data-testid="change-new" required type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("changePassword.confirm")}</label>
              <input data-testid="change-confirm" required type="password" minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-zinc-200 rounded-lg text-sm">{tc("actions.cancel")}</button>
              <button data-testid="change-submit" type="submit" disabled={saving} className="px-4 py-2 bg-zinc-950 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {t("changePassword.submit")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
